import { useEffect, useRef, useState } from "react";
import Document from "../../models/document";
import { getAbsoluteUrl } from "../../api/urls";
import { fetchAuthenticated } from "../../api/utils";
import { warningAlert } from "../utils/messages";
import { PdfPreview } from "./PdfPreviewModal";

const isPdfFile = (filePath?: string | null) => {
  const fileName = filePath?.split("/").pop() || "";
  return fileName.toLowerCase().endsWith(".pdf");
};

const usePdfPreview = () => {
  const [previewingIds, setPreviewingIds] = useState<Set<number>>(new Set());
  const [pdfPreview, setPdfPreview] = useState<PdfPreview | null>(null);
  const pdfPreviewUrlRef = useRef<string | null>(null);

  const closePdfPreview = () => {
    if (pdfPreviewUrlRef.current) {
      window.URL.revokeObjectURL(pdfPreviewUrlRef.current);
      pdfPreviewUrlRef.current = null;
    }
    setPdfPreview(null);
  };

  const handlePreviewPdfClick = async (previewDoc: Document) => {
    try {
      if (!previewDoc || !previewDoc.id) {
        throw new Error("Documento inválido");
      }

      if (!isPdfFile(previewDoc.filePath)) {
        return;
      }

      setPreviewingIds((prev) => new Set(prev).add(previewDoc.id!));

      const previewUrl = getAbsoluteUrl(
        `/documents/${previewDoc.id}/get_document_url/`
      );

      const previewResponse = await fetchAuthenticated(previewUrl, {
        method: "GET",
      });

      if (!previewResponse.ok) {
        throw new Error(
          `Error HTTP ${previewResponse.status}: ${previewResponse.statusText}`
        );
      }

      const blob = await previewResponse.blob();
      const url = window.URL.createObjectURL(
        new Blob([blob], { type: "application/pdf" })
      );

      if (pdfPreviewUrlRef.current) {
        window.URL.revokeObjectURL(pdfPreviewUrlRef.current);
      }

      pdfPreviewUrlRef.current = url;
      setPdfPreview({
        url,
        fileName: previewDoc.filePath?.split("/").pop() || "documento.pdf",
      });
    } catch (error) {
      console.error("Error al previsualizar:", error);
      warningAlert(
        `Error al previsualizar: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    } finally {
      setPreviewingIds((prev) => {
        const newSet = new Set(prev);
        if (previewDoc?.id) {
          newSet.delete(previewDoc.id);
        }
        return newSet;
      });
    }
  };

  useEffect(() => {
    return () => {
      if (pdfPreviewUrlRef.current) {
        window.URL.revokeObjectURL(pdfPreviewUrlRef.current);
      }
    };
  }, []);

  return {
    closePdfPreview,
    handlePreviewPdfClick,
    isPdfFile,
    pdfPreview,
    previewingIds,
  };
};

export default usePdfPreview;
